/**
 * Cuota de almacenamiento por consultorio: solo radiografías e imágenes clínicas.
 * Límites y caché de uso solo en memoria (sesión); datos en API / PostgreSQL.
 */

import { getToken, COOKIE_KEYS } from './cookies';

/** Opciones al crear/editar consultorio (admin) */
export const STORAGE_LIMIT_OPTIONS_MB = [250, 500, 1024, 2048, 3072, 5120];

const DEFAULT_LIMIT_MB = 1024;

/** tenantId → límite MB (desde JWT /me o listado admin) */
const limitByTenant = new Map();
/** tenantId → { bytes, at } */
const usageCacheByTenant = new Map();

export function formatStorageMb(mb) {
  const n = Number(mb) || 0;
  if (n >= 1024) {
    const gb = n / 1024;
    return gb % 1 === 0 ? `${gb} GB` : `${gb.toFixed(1)} GB`;
  }
  return `${n} MB`;
}

export function formatStorageBytes(bytes) {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes || 0} B`;
}

export function cacheTenantStorageLimit(tenantId, storageLimitMb) {
  if (!tenantId) return;
  limitByTenant.set(String(tenantId), Number(storageLimitMb) || DEFAULT_LIMIT_MB);
}

export function getTenantStorageLimitMb(tenantId, fallbackMb = DEFAULT_LIMIT_MB) {
  if (!tenantId) return fallbackMb;
  const v = limitByTenant.get(String(tenantId));
  if (v && STORAGE_LIMIT_OPTIONS_MB.includes(v)) return v;
  return fallbackMb;
}

export function getTenantStorageLimitBytes(tenantId, fallbackMb) {
  return getTenantStorageLimitMb(tenantId, fallbackMb) * 1024 * 1024;
}

export function estimateDataUrlBytes(dataUrl, explicitBytes) {
  if (explicitBytes != null && explicitBytes > 0) return Number(explicitBytes);
  const s = String(dataUrl || '');
  if (!s || s.length < 80) return 0;
  const comma = s.indexOf(',');
  const b64 = comma >= 0 ? s.slice(comma + 1) : s;
  return Math.ceil((b64.length * 3) / 4);
}

export async function estimateRadiografiaBytes(record = {}) {
  if (record.archivoBytes > 0) return record.archivoBytes;
  const inline = record.archivoDataUrl || record.dataUrl || '';
  if (inline && String(inline).length > 64) {
    return estimateDataUrlBytes(inline);
  }
  const sid = record.archivoStorageId || record.storageId;
  if (sid) {
    try {
      const { getClinicalFile } = await import('./clinicalFileStorage');
      const blob = await getClinicalFile(sid);
      if (blob?.category && blob.category !== 'radiografia' && blob.category !== 'imagen_clinica') {
        return 0;
      }
      if (blob?.byteSize > 0) return blob.byteSize;
      if (blob?.dataUrl) return estimateDataUrlBytes(blob.dataUrl);
    } catch {
      /* noop */
    }
  }
  return 0;
}

/** Bytes usados por archivos clínicos del tenant (API). */
export async function computeTenantStorageUsedBytes(tenantId) {
  if (!tenantId) return 0;
  try {
    const adminToken = getToken(COOKIE_KEYS.ADMIN_TOKEN);
    const customerToken = getToken(COOKIE_KEYS.AUTH_TOKEN);

    if (adminToken && !customerToken) {
      const api = (await import('../api/client')).default;
      const { data } = await api.get(`/tenants/${tenantId}/storage-usage`);
      return Number(data?.bytes) || 0;
    }

    const { fetchStorageUsage } = await import('../api/consultorioClinical');
    const data = await fetchStorageUsage();
    return Number(data?.bytes) || 0;
  } catch {
    return 0;
  }
}

export function cacheTenantStorageUsage(tenantId, usedBytes) {
  if (!tenantId) return;
  usageCacheByTenant.set(String(tenantId), {
    bytes: Number(usedBytes) || 0,
    at: new Date().toISOString(),
  });
}

export function getCachedTenantStorageUsage(tenantId) {
  const entry = usageCacheByTenant.get(String(tenantId));
  return entry?.bytes ?? null;
}

export async function refreshTenantStorageUsageCache(tenantId) {
  const used = await computeTenantStorageUsedBytes(tenantId);
  cacheTenantStorageUsage(tenantId, used);
  return used;
}

/** Archivos clínicos ya se registran en servidor; solo refresca caché en memoria. */
export function registerClinicalMediaEntry({ tenantId }) {
  if (!tenantId) return;
  refreshTenantStorageUsageCache(tenantId).catch(() => {});
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('consultorio-storage-changed'));
  }
}

export async function checkTenantStorageQuota(tenantId, additionalBytes, options = {}) {
  const limitMb = options.limitMb ?? getTenantStorageLimitMb(tenantId, options.fallbackMb);
  const limitBytes = limitMb * 1024 * 1024;
  const usedBytes = await computeTenantStorageUsedBytes(tenantId);
  const next = usedBytes + (Number(additionalBytes) || 0);
  if (next <= limitBytes) {
    return { ok: true, usedBytes, limitBytes, limitMb };
  }
  const falta = next - limitBytes;
  return {
    ok: false,
    usedBytes,
    limitBytes,
    limitMb,
    message: `Espacio insuficiente para radiografías/imágenes. Usado ${formatStorageBytes(usedBytes)} de ${formatStorageMb(limitMb)}. Este archivo requiere ~${formatStorageBytes(additionalBytes)} y faltan ~${formatStorageBytes(falta)}. Las firmas del consentimiento no consumen esta cuota. Elimine estudios antiguos o pida ampliar el límite.`,
  };
}

export function storageUsagePercent(usedBytes, limitBytes) {
  if (!limitBytes) return 0;
  return Math.min(100, Math.round((usedBytes / limitBytes) * 100));
}
